import { Traject } from "@/entities/dossier-details";
import { TrajectenGewijzigdActieDto } from "@/shared/types";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class TrajectenGewijzigdActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(reason: string) {
        super("TrajectenGewijzigd", "Gewijzigd", reason);
    }

    public static geenNieuweWaardeVoorGewijzigd(): TrajectenGewijzigdActieTransformatieError {
        return new TrajectenGewijzigdActieTransformatieError(`nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }
}

export type TrajectenGewijzigdActie = BaseActie & {
    type: "TrajectenGewijzigd";
    nieuweWaarde: Traject[];
    actieTrigger: "SpoorwebLocatieWijziging" | "SpoorwebGekoppeldAanDossier";
};

export const TrajectenGewijzigdActie = {
    fromDto: (dto: TrajectenGewijzigdActieDto): TrajectenGewijzigdActie => {
        if (!dto.nieuweWaarde) {
            throw TrajectenGewijzigdActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }

        return {
            id: dto.id,
            type: "TrajectenGewijzigd",
            timelineContent: {
                titel: "Trajecten wijzigen",
                beschrijving:
                    dto.actieTrigger === "SpoorwebLocatieWijziging"
                        ? "Omdat de incidentlocatie in Spoorweb gewijzigd is"
                        : "Omdat een Spoorweb-incident aan het dossier gekoppeld is",
                actieTekst: "Controleer en bevestig trajecten",
            },
            dialogContent: {
                titel: "Update trajecten",
                beschrijving:
                    dto.actieTrigger === "SpoorwebLocatieWijziging"
                        ? "De incidentlocatie in Spoorweb is gewijzigd, dit leidt tot andere trajecten. Wil je deze trajecten overnemen in het SEIN dossier?"
                        : "Er is een Spoorweb-incident aan het dossier gekoppeld. Wil je de trajecten van dit incident overnemen in het SEIN dossier?",
                weigerenTitel: "Update trajecten weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de update van de trajecten wilt weigeren?",
                weigerenActieTekst: "Trajecten weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: dto.wijzigingType,
            nieuweWaarde: dto.nieuweWaarde.map(Traject.fromDto),
            actieTrigger: dto.actieTrigger,
        };
    },
    update: (actie: TrajectenGewijzigdActie, trajecten: Traject[]): TrajectenGewijzigdActie => {
        return { ...actie, nieuweWaarde: trajecten };
    },
    toDto: (actie: TrajectenGewijzigdActie): TrajectenGewijzigdActieDto => ({
        id: actie.id,
        type: actie.type,
        verstuurdOpDatumTijd: actie.verstuurdOpDatumTijd.toISOString(),
        status: actie.status,
        toelichting: actie.toelichting,
        nieuweWaarde: actie.nieuweWaarde as TrajectenGewijzigdActieDto["nieuweWaarde"],
        wijzigingType: actie.wijzigingType,
        actieTrigger: actie.actieTrigger,
    }),
};
